import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Brush,
} from "recharts";
import "../loader.css";
import "./Custom.css";

function UserMainContent() {
  const [chartData, setChartData] = useState([]);
  const [chatLog, setChatLog] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalChars, setTotalChars] = useState(0);
  const [avgPerDay, setAvgPerDay] = useState(0);
  const [caseLines, setCaseLines] = useState([]);

  const today = new Date();
  const monthLabel = today.toLocaleString("en-US", { month: "long", year: "numeric" });
  const lineColors = ["#42a5f5", "#66bb6a", "#ffca28", "#ef5350", "#ab47bc", "#26c6da"];

  const getToken = () => {
    try {
      const raw = localStorage.getItem("state");
      if (raw) return JSON.parse(raw).auth?.token;
    } catch (e) {}
    return null;
  };

  useEffect(() => {
    const token = getToken();
    const headers = { ...(token && { Authorization: `Bearer ${token}` }) };

    Promise.all([
      axios.get(`${process.env.REACT_APP_API_URL}/AppUser/GetDashboardUsage`, { headers }),
      axios.get(`${process.env.REACT_APP_API_URL}/AppUser/GetDashboardChatLog`, { headers }),
    ])
      .then(([usageRes, chatRes]) => {
        const raw = usageRes.data || [];

        // Unique case names for chart lines
        const names = [...new Set(raw.map((r) => r.case_Name || r.Case_Name))].filter(Boolean);
        setCaseLines(names);

        // Build date-keyed map from actual data
        const dateMap = {};
        raw.forEach((r) => {
          const date = r.usage_Date || r.Usage_Date;
          const name = r.case_Name || r.Case_Name;
          const chars = r.characters_Used || r.Characters_Used || 0;
          if (!dateMap[date]) dateMap[date] = { date };
          dateMap[date][name] = (dateMap[date][name] || 0) + chars;
        });

        // Build FULL month skeleton -- every day gets an entry, missing days get 0
        const year = today.getFullYear();
        const month = today.getMonth();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const allDays = [];
        for (let d = 1; d <= daysInMonth; d++) {
          const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
          const existing = dateMap[dateStr] ? { ...dateMap[dateStr] } : { date: dateStr };
          names.forEach((name) => {
            if (existing[name] === undefined) existing[name] = 0;
          });
          allDays.push(existing);
        }

        const total = raw.reduce((sum, r) => sum + (r.characters_Used || r.Characters_Used || 0), 0);

        setChartData(allDays);
        setTotalChars(total);
        setAvgPerDay(daysInMonth > 0 ? (total / daysInMonth).toFixed(2) : 0);
        setChatLog(chatRes.data || []);
      })
      .catch((err) => console.error("Dashboard load failed", err))
      .finally(() => setIsLoading(false));
  }, []);

  return (
    <div className="page-content-wrapper">
      {isLoading ? (
        <div className="page-content d-flex justify-content-center align-items-center">
          <div className="my-wrapper">
            <div className="spinner center">
              {[...Array(12)].map((_, i) => <div key={i} className="spinner-blade" />)}
            </div>
          </div>
        </div>
      ) : (
        <div className="page-content">
          <div className="page-bar">
            <div className="page-title-breadcrumb">
              <div className="pull-left">
                <div className="page-title">Dashboard</div>
              </div>
            </div>
          </div>

          {/* CHART */}
          <div className="row">
            <div className="col-md-12">
              <div className="card">
                <div className="card-head">
                  <header>
                    <h5 className="mb-0">Character Usage — {monthLabel}</h5>
                  </header>
                </div>
                <div className="card-body">
                  <div style={{ position: "relative" }}>
                    <div className="stats-card">
                      <h6>📊 Summary</h6>
                      <div className="stat-row">
                        <span className="stat-label">Days in Month:</span>
                        <span className="stat-value">{chartData.length}</span>
                      </div>
                      <div className="stat-row">
                        <span className="stat-label">Total Usage:</span>
                        <span className="stat-value">{totalChars.toLocaleString()}</span>
                      </div>
                      <div className="stat-row">
                        <span className="stat-label">Avg per Day:</span>
                        <span className="stat-value">{avgPerDay}</span>
                      </div>
                    </div>
                    <ResponsiveContainer width="100%" height={400}>
                      <LineChart data={chartData} margin={{ top: 20, right: 40, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                        <XAxis
                          dataKey="date"
                          stroke="#666"
                          tickFormatter={(val) => val.slice(5)} // show MM-DD only
                        />
                        <YAxis stroke="#666" />
                        <Tooltip contentStyle={{ backgroundColor: "#fff", border: "1px solid #ccc" }} />
                        <Legend />
                        {caseLines.length > 0 ? (
                          caseLines.map((name, i) => (
                            <Line
                              key={name}
                              type="monotone"
                              dataKey={name}
                              stroke={lineColors[i % lineColors.length]}
                              strokeWidth={2}
                              name={name}
                              dot={false}
                            />
                          ))
                        ) : (
                          // No cases yet -- show a flat zero line as placeholder
                          <Line type="monotone" dataKey="__empty__" stroke="#ccc" strokeWidth={1} dot={false} name="No data" />
                        )}
                        <Brush dataKey="date" height={30} stroke="#8884d8" />
                      </LineChart>
                    </ResponsiveContainer>
                    {caseLines.length === 0 && (
                      <div className="text-center" style={{ marginTop: "-60px", paddingBottom: "80px", color: "#999" }}>
                        No usage recorded yet this month.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* CHAT LOG */}
          <div className="row mt-4">
            <div className="col-md-12">
              <div className="card card-box">
                <div className="card-head">
                  <header><h5 className="mb-0">Recent Chat History</h5></header>
                </div>
                <div className="card-body">
                  {chatLog.length > 0 ? (
                    <div className="table-responsive">
                      <table className="table table-bordered table-hover">
                        <thead>
                          <tr>
                            <th style={{ whiteSpace: "nowrap" }}>Date / Time</th>
                            <th>Case</th>
                            <th>You said</th>
                            <th>Response</th>
                          </tr>
                        </thead>
                        <tbody>
                          {chatLog.map((t) => (
                            <tr key={t.id || t.Id}>
                              <td style={{ whiteSpace: "nowrap" }}>{t.timestamp || t.Timestamp}</td>
                              <td>
                                <span style={{
                                  backgroundColor: "#1e2e38",
                                  color: "#fff",
                                  padding: "4px 8px",
                                  borderRadius: "4px",
                                  fontSize: "12px"
                                }}>
                                  {t.case_Name || t.Case_Name}
                                </span>
                              </td>
                              <td>{t.question || t.Question}</td>
                              <td>{t.responces || t.Responces}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-5">
                      <i className="fa fa-comments fa-3x" style={{ color: "#ccc" }} />
                      <p className="mt-3" style={{ color: "#999" }}>No chat history yet.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserMainContent;